/**
 * Debounced WebSocket listener for onboarding updates.
 *
 * Multiple workers can finish within milliseconds of each other (e.g. ai-verify
 * approves → triggers TRIGGER_FOLLOWUP + CREATE_COMPLIANCE_RECORD + CHECK_ACTIVATION).
 * This hook coalesces rapid events into a single refetch after 500ms of quiet.
 *
 * The in-app toast notification fires immediately on the first event (not debounced).
 */

import { useEffect, useRef } from "react";

import { useNotification } from "@/lib/notifications";
import { useWebSocketEvent } from "@/lib/websocket";

export function useOnboardingRefresh(rnId: string, onRefresh: () => void) {
    const timerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
    const onRefreshRef = useRef(onRefresh);
    onRefreshRef.current = onRefresh;
    const { showNotification } = useNotification();

    useWebSocketEvent("onboarding:updated", (data) => {
        if (String(data.rnId) !== rnId) {
            return;
        }

        // Show toast immediately (first event wins — rapid duplicates are fine,
        // the notification component replaces the current one)
        showNotification(data.message, data.status);

        // Debounce the data refetch (coalesce rapid cascading events)
        if (timerRef.current) {
            clearTimeout(timerRef.current);
        }
        timerRef.current = setTimeout(() => onRefreshRef.current(), 500);
    });

    useEffect(() => {
        return () => {
            if (timerRef.current) {
                clearTimeout(timerRef.current);
            }
        };
    }, []);
}
